import type { BibleBookmark } from '@/types/bookmark';
import type { BibleHighlight } from '@/types/highlight';
import type {
  CompleteBookmarkDocument,
  CompleteHighlightDocument,
  CompletePrayerDocument,
  ContentEntityType,
  LocalContentEntityV1,
  OrdinaryContentEnvelopeV1,
} from '@/types/content-lifecycle';
import type { Prayer } from '@/types/prayer';
import {
  hasPositiveUnsyncedOwnerWorkRow,
  installAuthoritativeOwnerWorkspaceAtomically,
  type AuthoritativeOwnerWorkspaceInstall,
} from './db';
import { hasNonEmptyRecoveryText } from './content-escrow/semantic';
import {
  bookmarkFromCompleteDocument,
  classifyOrdinaryEnvelope,
  highlightFromCompleteDocument,
  prayerFromCompleteDocument,
} from './content-lifecycle/contracts';
import { getSupabase } from './supabase';
import { runExclusiveOwnerTransition } from './owner-transition';
import { inspectUnboundContentOwnerProfile } from './content-owner';

const OWNER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const PULL_PAGE_LIMIT = 200;
const MAX_PULL_PAGES = 10_000;

export type OwnerBootstrapFailureCode =
  | 'authentication-changed'
  | 'connection-failed'
  | 'invalid-server-state';

export class OwnerBootstrapError extends Error {
  readonly code: OwnerBootstrapFailureCode;

  constructor(code: OwnerBootstrapFailureCode, cause?: unknown) {
    super(code, { cause });
    this.name = 'OwnerBootstrapError';
    this.code = code;
  }
}

export interface OwnerSnapshotTransport {
  pull(
    expectedOwner: string,
    afterRevision: number,
    pageLimit: number,
  ): Promise<unknown>;
}

type PullPage = {
  observedHighWater: number;
  envelopes: unknown[];
};

function isExactPullPage(value: unknown): value is PullPage {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort();
  return (
    JSON.stringify(keys) === JSON.stringify(['envelopes', 'observedHighWater']) &&
    Number.isSafeInteger(record.observedHighWater) &&
    Number(record.observedHighWater) >= 0 &&
    Array.isArray(record.envelopes) &&
    record.envelopes.length <= PULL_PAGE_LIMIT
  );
}

function defaultTransport(): OwnerSnapshotTransport {
  const client = getSupabase() as unknown as {
    rpc(name: string, args: Record<string, unknown>): Promise<{
      data: unknown;
      error: { message: string } | null;
    }>;
  } | null;
  if (!client) throw new OwnerBootstrapError('connection-failed');
  return {
    async pull(expectedOwner, afterRevision, pageLimit) {
      const { data, error } = await client.rpc('pull_content_changes_v1', {
        expected_owner: expectedOwner,
        after_revision: afterRevision,
        page_limit: pageLimit,
      });
      if (error) throw new OwnerBootstrapError('connection-failed', error.message);
      return data;
    },
  };
}

function entityKey(envelope: OrdinaryContentEnvelopeV1): string {
  return `${envelope.entityType}\n${envelope.entityId}`;
}

function validateServerTransition(
  prior: OrdinaryContentEnvelopeV1 | undefined,
  next: OrdinaryContentEnvelopeV1,
): boolean {
  if (!prior) {
    return next.opKind === 'create' &&
      next.expectedBaseRevision === null &&
      next.payload.kind === 'live';
  }
  if (next.expectedBaseRevision !== prior.contentRevision) return false;
  if (next.opKind === 'update') {
    return prior.payload.kind === 'live' && next.payload.kind === 'live';
  }
  if (next.opKind === 'delete') {
    return prior.payload.kind === 'live' && next.payload.kind === 'tombstone';
  }
  if (next.opKind === 'restore') {
    return prior.payload.kind === 'tombstone' && next.payload.kind === 'live';
  }
  return false;
}

export type ValidatedOwnerSnapshot = AuthoritativeOwnerWorkspaceInstall & {
  envelopes: OrdinaryContentEnvelopeV1[];
};

/**
 * Validate the complete contiguous owner history and fold it into a clean
 * local baseline. No local write occurs in this function.
 */
export async function validateAndFoldOwnerSnapshot(
  ownerId: string,
  highWater: number,
  rawEnvelopes: unknown[],
  isAuthenticatedOwnerCurrent: () => boolean,
): Promise<ValidatedOwnerSnapshot> {
  if (
    !OWNER_ID_RE.test(ownerId) ||
    !Number.isSafeInteger(highWater) ||
    highWater < 0 ||
    rawEnvelopes.length !== highWater ||
    !isAuthenticatedOwnerCurrent()
  ) throw new OwnerBootstrapError('invalid-server-state');

  const envelopes: OrdinaryContentEnvelopeV1[] = [];
  const tips = new Map<string, OrdinaryContentEnvelopeV1>();
  const opIds = new Set<string>();
  for (let index = 0; index < rawEnvelopes.length; index += 1) {
    if (!isAuthenticatedOwnerCurrent()) {
      throw new OwnerBootstrapError('authentication-changed');
    }
    const classified = await classifyOrdinaryEnvelope(rawEnvelopes[index], ownerId);
    if (classified.kind !== 'recognized') {
      throw new OwnerBootstrapError('invalid-server-state');
    }
    const envelope = classified.envelope;
    const key = entityKey(envelope);
    if (
      envelope.contentRevision !== index + 1 ||
      opIds.has(envelope.opId) ||
      !validateServerTransition(tips.get(key), envelope)
    ) throw new OwnerBootstrapError('invalid-server-state');
    opIds.add(envelope.opId);
    tips.set(key, envelope);
    envelopes.push(envelope);
  }
  if (!isAuthenticatedOwnerCurrent()) {
    throw new OwnerBootstrapError('authentication-changed');
  }

  const prayers: Prayer[] = [];
  const bookmarks: BibleBookmark[] = [];
  const highlights: BibleHighlight[] = [];
  const entities: LocalContentEntityV1[] = [];
  for (const envelope of tips.values()) {
    if (envelope.payload.kind === 'tombstone') {
      entities.push({
        ownerId,
        entityType: envelope.entityType,
        entityId: envelope.entityId,
        state: 'tombstoned',
        installedEnvelope: envelope,
        visibleDocument: null,
        pending: null,
        mutationSeq: envelope.mutationSeq,
        localBaseRevision: envelope.contentRevision,
        heldReason: null,
        quarantineReason: null,
        recovery: 'tombstone-installed',
      });
      continue;
    }
    const document = envelope.payload.document;
    entities.push({
      ownerId,
      entityType: envelope.entityType,
      entityId: envelope.entityId,
      state: 'clean',
      installedEnvelope: envelope,
      visibleDocument: document,
      pending: null,
      mutationSeq: envelope.mutationSeq,
      localBaseRevision: envelope.contentRevision,
      heldReason: null,
      quarantineReason: null,
      recovery: 'installed-remote',
    });
    if (envelope.entityType === 'prayer') {
      prayers.push(prayerFromCompleteDocument(document as CompletePrayerDocument));
    } else if (envelope.entityType === 'bookmark') {
      bookmarks.push(bookmarkFromCompleteDocument(document as CompleteBookmarkDocument));
    } else {
      highlights.push(highlightFromCompleteDocument(document as CompleteHighlightDocument));
    }
  }
  entities.sort((left, right) => {
    const leftKey = `${left.entityType}\n${left.entityId}`;
    const rightKey = `${right.entityType}\n${right.entityId}`;
    return leftKey.localeCompare(rightKey);
  });
  prayers.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  bookmarks.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  highlights.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return { ownerId, highWater, envelopes, prayers, bookmarks, highlights, entities };
}

/** Fetch every page up to one validated final high-water mark. */
export async function fetchAuthenticatedOwnerSnapshot(
  ownerId: string,
  isAuthenticatedOwnerCurrent: () => boolean,
  transport: OwnerSnapshotTransport = defaultTransport(),
): Promise<ValidatedOwnerSnapshot> {
  if (!OWNER_ID_RE.test(ownerId) || !isAuthenticatedOwnerCurrent()) {
    throw new OwnerBootstrapError('authentication-changed');
  }
  const envelopes: unknown[] = [];
  let afterRevision = 0;
  let observedHighWater = 0;
  for (let pageNumber = 0; pageNumber < MAX_PULL_PAGES; pageNumber += 1) {
    if (!isAuthenticatedOwnerCurrent()) {
      throw new OwnerBootstrapError('authentication-changed');
    }
    let rawPage: unknown;
    try {
      rawPage = await transport.pull(ownerId, afterRevision, PULL_PAGE_LIMIT);
    } catch (error) {
      if (error instanceof OwnerBootstrapError) throw error;
      throw new OwnerBootstrapError('connection-failed', error);
    }
    if (!isAuthenticatedOwnerCurrent()) {
      throw new OwnerBootstrapError('authentication-changed');
    }
    if (
      !isExactPullPage(rawPage) ||
      rawPage.observedHighWater < observedHighWater ||
      rawPage.observedHighWater < afterRevision
    ) throw new OwnerBootstrapError('invalid-server-state');
    observedHighWater = rawPage.observedHighWater;
    for (const raw of rawPage.envelopes) {
      const expectedRevision = afterRevision + 1;
      if (
        !raw || typeof raw !== 'object' ||
        (raw as { contentRevision?: unknown }).contentRevision !== expectedRevision ||
        expectedRevision > observedHighWater
      ) throw new OwnerBootstrapError('invalid-server-state');
      envelopes.push(raw);
      afterRevision = expectedRevision;
    }
    if (afterRevision === observedHighWater) {
      return validateAndFoldOwnerSnapshot(
        ownerId,
        observedHighWater,
        envelopes,
        isAuthenticatedOwnerCurrent,
      );
    }
    if (rawPage.envelopes.length === 0) {
      throw new OwnerBootstrapError('invalid-server-state');
    }
  }
  throw new OwnerBootstrapError('invalid-server-state');
}

const UNSYNCED_BROWSER_KEYS = [
  'adia-prayer-draft',
  'adia-interrupted-interim',
] as const;

/** Positive authored browser work that must block a bound-owner switch. */
export function hasPositiveUnsyncedBrowserWork(
  storage: Storage | null | undefined =
    typeof localStorage === 'undefined' ? undefined : localStorage,
): boolean {
  if (!storage) return true;
  try {
    return UNSYNCED_BROWSER_KEYS.some((key) => {
      const value = storage.getItem(key);
      return value !== null && hasNonEmptyRecoveryText({ key, value });
    });
  } catch {
    return true;
  }
}

export async function hasPositiveUnsyncedOwnerWork(ownerId: string): Promise<boolean> {
  if (hasPositiveUnsyncedBrowserWork()) return true;
  return hasPositiveUnsyncedOwnerWorkRow(ownerId);
}

export type AdoptOwnerWorkspaceResult =
  | { status: 'installed'; snapshot: ValidatedOwnerSnapshot }
  | { status: 'pending-work' }
  | { status: 'protected-content' };

/**
 * Fetch first, validate completely, then enter the one destructive local
 * transaction. Unbound legacy bytes are intentionally treated as a cache.
 */
export async function adoptAuthenticatedOwnerWorkspace(options: {
  ownerId: string;
  previousOwnerId: string | null;
  isAuthenticatedOwnerCurrent: () => boolean;
  transport?: OwnerSnapshotTransport;
  abortSignal?: AbortSignal;
  testInterruptAfterClear?: boolean;
}): Promise<AdoptOwnerWorkspaceResult> {
  return runExclusiveOwnerTransition(options.ownerId, async () => {
    if (
      options.previousOwnerId === null &&
      await inspectUnboundContentOwnerProfile() === 'protected'
    ) {
      return { status: 'protected-content' };
    }
    const switching = options.previousOwnerId !== null &&
      options.previousOwnerId !== options.ownerId;
    if (
      switching &&
      await hasPositiveUnsyncedOwnerWork(options.previousOwnerId as string)
    ) return { status: 'pending-work' };

    const snapshot = await fetchAuthenticatedOwnerSnapshot(
      options.ownerId,
      options.isAuthenticatedOwnerCurrent,
      options.transport,
    );
    if (!options.isAuthenticatedOwnerCurrent()) {
      throw new OwnerBootstrapError('authentication-changed');
    }
    await installAuthoritativeOwnerWorkspaceAtomically(snapshot, {
      expectedPreviousOwnerId: options.previousOwnerId,
      ...(switching
        ? { requireNoUnsyncedOwnerId: options.previousOwnerId as string }
        : {}),
      stillAuthorizedAndSafe: () =>
        options.isAuthenticatedOwnerCurrent() &&
        (!switching || !hasPositiveUnsyncedBrowserWork()),
      abortSignal: options.abortSignal,
      testInterruptAfterClear: options.testInterruptAfterClear,
    });
    return { status: 'installed', snapshot };
  });
}

/** Used by focused probes to assert the three entity types stay closed. */
export const OWNER_SNAPSHOT_ENTITY_TYPES: readonly ContentEntityType[] = [
  'prayer',
  'bookmark',
  'highlight',
];
